import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import {
  ChevronDown,
  User,
  Shield,
  Users,
  Settings,
  Eye,
  AlertTriangle,
} from "lucide-react";
import { useEnvironment } from "@/hooks/useEnvironment";
import { listProfiles, type Profile as ApiProfile } from "@/services/profilesService";

interface ProfileDisplay {
  id: string;
  name: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
}

// Mapeamento de ícones e cores por nome de perfil
const getProfileDisplay = (profile: ApiProfile): ProfileDisplay => {
  const normalizedName = profile.name.toLowerCase();

  if (normalizedName === "administrador") {
    return {
      id: profile.id,
      name: profile.name,
      description: profile.description,
      icon: Settings,
      color: "bg-red-500",
    };
  }

  if (normalizedName === "admin cliente") {
    return {
      id: profile.id,
      name: profile.name,
      description: profile.description,
      icon: Shield,
      color: "bg-orange-500",
    };
  }

  if (normalizedName === "analista") {
    return {
      id: profile.id,
      name: profile.name,
      description: profile.description,
      icon: Eye,
      color: "bg-green-500",
    };
  }

  if (normalizedName === "cliente") {
    return {
      id: profile.id,
      name: profile.name,
      description: profile.description,
      icon: Users,
      color: "bg-purple-500",
    };
  }

  if (normalizedName === "usuário workspace") {
    return {
      id: profile.id,
      name: profile.name,
      description: profile.description,
      icon: User,
      color: "bg-blue-500",
    };
  }

  if (normalizedName === "suporte") {
    return {
      id: profile.id,
      name: profile.name,
      description: profile.description,
      icon: Shield,
      color: "bg-gray-500",
    };
  }

  // Padrão
  return {
    id: profile.id,
    name: profile.name,
    description: profile.description,
    icon: User,
    color: "bg-slate-500",
  };
};

interface ProfileSelectorProps {
  currentProfile: string;
  onProfileChange: (profileId: string) => void;
}

export const ProfileSelector: React.FC<ProfileSelectorProps> = ({
  currentProfile,
  onProfileChange,
}) => {
  const { showProfileSelector, isProduction } = useEnvironment();
  const [profiles, setProfiles] = useState<ProfileDisplay[]>([]);
  const [loading, setLoading] = useState(true);

  // Buscar perfis da API
  useEffect(() => {
    const fetchProfiles = async () => {
      try {
        const response = await listProfiles({ is_active: true });
        if (response.success && response.data) {
          const profileDisplays = response.data.data.map(getProfileDisplay);
          setProfiles(profileDisplays);
        }
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };

    if (!isProduction && showProfileSelector) {
      fetchProfiles();
    }
  }, [isProduction, showProfileSelector]);

  const currentProfileData = profiles.find((p) => p.id === currentProfile);
  const CurrentIcon = currentProfileData?.icon || User;

  // Em produção, não renderizar o seletor de perfis
  if (isProduction || !showProfileSelector) {
    return null;
  }

  if (loading) {
    return null;
  }

  return (
    <div className="flex items-center space-x-2">
      {/* Aviso de desenvolvimento */}
      <div className="flex items-center space-x-1 text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded-md border border-amber-200">
        <AlertTriangle className="h-3 w-3" />
        <span className="font-medium">DEV</span>
      </div>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            className="flex items-center space-x-2 hover:bg-primary/10"
            disabled={profiles.length === 0}
          >
            <div
              className={`w-2 h-2 rounded-full ${
                currentProfileData?.color || "bg-gray-500"
              }`}
            />
            <CurrentIcon className="h-4 w-4" />
            <span className="text-sm font-medium">
              {currentProfileData?.name || "Selecionar Perfil"}
            </span>
            <ChevronDown className="h-3 w-3" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="w-64 bg-popover border shadow-lg z-50"
        >
          <DropdownMenuLabel className="text-xs text-muted-foreground">
            Selecionar Perfil de Usuário
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {profiles.map((profile) => {
            const ProfileIcon = profile.icon;
            const isSelected = profile.id === currentProfile;

            return (
              <DropdownMenuItem
                key={profile.id}
                onClick={() => onProfileChange(profile.id)}
                className="cursor-pointer"
              >
                <div className="flex items-center space-x-3 w-full">
                  <div className={`w-2 h-2 rounded-full ${profile.color}`} />
                  <ProfileIcon className="h-4 w-4" />
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium">
                        {profile.name}
                      </span>
                      {isSelected && (
                        <Badge variant="secondary" className="text-xs">
                          Atual
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {profile.description}
                    </p>
                  </div>
                </div>
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
